import { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Typography,
  Chip,
  Alert,
  Grid,
  CircularProgress,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import moment from "moment";

/**
 * PartnerPaymentConfirmation Tab
 * Shows service partner payments (PROCESSING/TRANSFER_INITIATED status)
 * VIEW-ONLY - Admin cannot approve or take any action
 */

// Utility function to format currency
const formatCurrency = (amount, currency = 'AED') => {
  if (amount === null || amount === undefined) return '—';
  return new Intl.NumberFormat('en-AE', { style: 'currency', currency }).format(amount);
};

// Get status color based on payment status
const getStatusColor = (status) => {
  switch (status) {
    case 'COMPLETED':
    case 'PAID':
      return 'success';
    case 'PROCESSING':
    case 'TRANSFER_INITIATED':
      return 'warning';
    case 'PENDING':
      return 'info';
    case 'FAILED':
    case 'REJECTED':
      return 'error';
    default:
      return 'default';
  }
};

function ExpandableRow({ payment }) {
  const [open, setOpen] = useState(false);
  const hasTickets = payment.tickets && payment.tickets.length > 0;
  const ticketCount = payment.tickets?.length || 0;

  // Format status for display
  const formatStatus = (status) => {
    if (!status) return 'UNKNOWN';
    return status.replace(/_/g, ' ');
  };

  return (
    <>
      <TableRow
        sx={{
          "&:hover": { bgcolor: "grey.50" },
        }}
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
            disabled={!hasTickets}
          >
            {hasTickets ? (open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />) : null}
          </IconButton>
        </TableCell>
        {/* Transaction Number - use referenceNumber */}
        <TableCell sx={{ fontWeight: "bold", fontSize: "0.85rem" }}>
          {payment.referenceNumber || "—"}
        </TableCell>
        {/* Date - use createdAt or initiatedAt */}
        <TableCell>
          {payment.createdAt
            ? moment(payment.createdAt).format("YYYY-MM-DD HH:mm")
            : payment.initiatedAt
            ? moment(payment.initiatedAt).format("YYYY-MM-DD HH:mm")
            : "—"}
        </TableCell>
        <TableCell sx={{ fontWeight: "500" }}>
          {payment.partnerOrganisationName || "—"}
        </TableCell>
        {/* Source Bank - use sourceBankAccountName */}
        <TableCell>
          {payment.sourceBankAccountName || "—"}
        </TableCell>
        {/* Ticket Count - calculate from tickets array */}
        <TableCell align="center">
          <Chip
            label={`${ticketCount} ticket${ticketCount !== 1 ? 's' : ''}`}
            size="small"
            variant="outlined"
            color="primary"
          />
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: "bold", color: "primary.main" }}>
          {formatCurrency(payment.amount)}
        </TableCell>
        {/* Status - use status field */}
        <TableCell align="center">
          <Chip
            label={formatStatus(payment.status)}
            size="small"
            color={getStatusColor(payment.status)}
          />
        </TableCell>
      </TableRow>

      {/* Expandable Ticket Details */}
      {hasTickets && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 2, bgcolor: "grey.50", borderRadius: 1, p: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1.5 }}>
                  Tickets in this Transaction
                </Typography>
                <Grid container spacing={1}>
                  {payment.tickets.map((ticket, idx) => (
                    <Grid item xs={12} sm={6} md={4} key={ticket.ticketId || idx}>
                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: "white",
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: "bold", color: "primary.main" }}>
                          #{ticket.orgTicketNumber || ticket.ticketNumber}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {ticket.serviceName || "—"}
                        </Typography>
                        {/* Use ticketAmount for amount */}
                        <Typography variant="body2" sx={{ fontWeight: "500", mt: 0.5 }}>
                          {formatCurrency(ticket.ticketAmount)}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

const PartnerPaymentConfirmation = ({ data, loading }) => {
  // Handle loading state
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Handle empty data - data can be an array directly or an object with items
  const payments = Array.isArray(data) ? data : (data?.items || []);

  if (!payments || payments.length === 0) {
    return (
      <Alert severity="info">
        <Typography variant="body2">
          No service partner payments in transit at this time.
        </Typography>
      </Alert>
    );
  }

  // Calculate totals
  const totalAmount = payments.reduce((sum, p) => sum + (p.amount || p.totalAmount || 0), 0);
  const totalTickets = payments.reduce((sum, p) => sum + (p.ticketCount || 0), 0);

  return (
    <Box>
      {/* Summary Header */}
      <Box sx={{ mb: 2, p: 1.5, bgcolor: "info.light", borderRadius: 1, border: "1px solid", borderColor: "info.main" }}>
        <Typography variant="body2" sx={{ fontWeight: "bold", color: "info.dark" }}>
          Payments In Transit: {formatCurrency(totalAmount)} ({payments.length} transaction{payments.length !== 1 ? "s" : ""}, {totalTickets} ticket{totalTickets !== 1 ? "s" : ""})
        </Typography>
      </Box>

      {/* View-Only Notice */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="caption">
          This tab is view-only. Service partner payments are automatically processed and cannot be modified.
        </Typography>
      </Alert>

      {/* Payments Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead sx={{ bgcolor: "primary.main" }}>
            <TableRow>
              <TableCell width="40" sx={{ fontWeight: "bold", color: "white" }} />
              <TableCell sx={{ fontWeight: "bold", color: "white" }}>Transaction #</TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "white" }}>Date</TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "white" }}>Partner</TableCell>
              <TableCell sx={{ fontWeight: "bold", color: "white" }}>Source Bank</TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold", color: "white" }}>Tickets</TableCell>
              <TableCell align="right" sx={{ fontWeight: "bold", color: "white" }}>Amount</TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold", color: "white" }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payments.map((payment) => (
              <ExpandableRow key={payment.id || payment.bankTransactionId} payment={payment} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default PartnerPaymentConfirmation;
